/* eslint-disable react/no-array-index-key */
import { memo, useState, useCallback, FC, useMemo } from 'react';

import { IOTPInputProps } from './types';

import { Container } from './styles';

import { SingleOTPInput } from './SingleOTPInput';
import { Label } from '../Label';
import { ErrorMessage } from '../ErrorMessage';

const BaseOTPInput: FC<IOTPInputProps> = ({
  length = 6,
  disabled,
  onChange,
  error,
  label,
  value,
  size = 'md',
}) => {
  const baseOTPValues = useMemo(() => {
    return Array<string>(length).fill('');
  }, [length]);

  const [activeInput, setActiveInput] = useState(0);
  const [OTPValues, setOTPValues] = useState(baseOTPValues);

  const formatOTPValues = useCallback(() => {
    if (typeof value !== 'string') return OTPValues;

    const newOTPValues = value.split('');

    const formattedOTPValues = baseOTPValues.map((_, index) => {
      return (newOTPValues[index] || '').substring(0, 1) || '';
    });

    return formattedOTPValues;
  }, [OTPValues, baseOTPValues, value]);

  const handleOTPChange = useCallback(
    (otp: string[]) => {
      const otpValue = otp.join('').toUpperCase();
      onChange(otpValue);
    },
    [onChange]
  );

  const changeCodeAtFocus = useCallback(
    (str: string) => {
      setOTPValues(() => {
        const formattedOTPValues = formatOTPValues();

        formattedOTPValues[activeInput] = str[0]?.toUpperCase() || '';

        handleOTPChange(formattedOTPValues);

        return formattedOTPValues;
      });
    },
    [activeInput, formatOTPValues, handleOTPChange]
  );

  const focusInput = useCallback(
    (inputIndex: number) => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);

      setActiveInput(selectedIndex);
    },
    [length]
  );

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1);
  }, [activeInput, focusInput]);

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  const handleOnFocus = useCallback(
    (index: number) => () => {
      focusInput(index);
    },
    [focusInput]
  );

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget?.value || '';

      if (!val) {
        e.preventDefault();
        return;
      }

      changeCodeAtFocus(val);
      focusNextInput();
    },
    [changeCodeAtFocus, focusNextInput]
  );

  const onBlur = useCallback(() => {
    setActiveInput(-1);
  }, []);

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const pressedKey = e.key;

      switch (pressedKey) {
        case 'Backspace':
        case 'Delete': {
          e.preventDefault();
          if (OTPValues[activeInput]) {
            changeCodeAtFocus('');
          } else {
            focusPrevInput();
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          focusNextInput();
          break;
        }
        default: {
          if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
            e.preventDefault();
          }

          break;
        }
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, OTPValues]
  );

  const handleOnPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();

      const pastedData = e.clipboardData
        .getData('text/plain')
        .trim()
        .slice(0, length - activeInput)
        .split('');

      if (pastedData) {
        setOTPValues(() => {
          let nextFocusIndex = 0;
          const formattedOTPValues = formatOTPValues();

          formattedOTPValues.forEach((val, index) => {
            if (index >= activeInput) {
              const changedValue = pastedData.shift() || val;

              if (changedValue) {
                formattedOTPValues[index] = changedValue.toUpperCase();
                nextFocusIndex = index;
              }
            }
          });

          handleOTPChange(formattedOTPValues);
          setActiveInput(Math.min(nextFocusIndex + 1, length - 1));

          return formattedOTPValues;
        });
      }
    },
    [length, activeInput, formatOTPValues, handleOTPChange]
  );

  const finalOTPValues = useMemo(formatOTPValues, [formatOTPValues]);

  return (
    <Container>
      {!!label && <Label mb="xxs">{label}</Label>}

      <div>
        {Array(length)
          .fill('')
          .map((_, index) => (
            <SingleOTPInput
              hasError={!!error}
              size={size}
              key={`SingleOTPInput-${index}`}
              focus={activeInput === index}
              value={finalOTPValues[index] || ''}
              onFocus={handleOnFocus(index)}
              onChange={handleOnChange}
              onKeyDown={handleOnKeyDown}
              onBlur={onBlur}
              onPaste={handleOnPaste}
              disabled={disabled}
            />
          ))}
      </div>

      <ErrorMessage mt="xxs">{error}</ErrorMessage>
    </Container>
  );
};

export const OTPInput = memo(BaseOTPInput);
